import { ChangeEvent, KeyboardEvent, use, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { defaultPlaylistName, Hymn, SongList, SongListType, SongSlug } from '../../../../../common/src/model.ts';
import { LocalStorageKey, localStorageSet } from '../../../data/client_local_storage.ts';
import { getSongListFromHymnal } from '../../../data/hymnals.ts';
import { setSongTempoForOrg, useCurrentOrg } from '../../../data/use_current_org.tsx';
import { useCustomMusic } from '../../../data/use_custom_music.tsx';
import { HymnalsContext } from '../../../data/use_hymnals.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { PlayButton } from '../../../player/play_button.tsx';
import { SequencerContext } from '../../../player/sequencer.tsx';
import { addCustomSong } from '../../../util/add_song.tsx';
import { NetworkError } from '../../../util/network.ts';
import { PdfViewer } from '../../../util/pdf_viewer.tsx';
import { getPlatform, Platform } from '../../../util/platform.ts';
import { SearchContext } from '../../../util/search_control.tsx';
import { hymnalEntryPadding, QuarterNote } from '../../../util/shared.tsx';
import { useDebouncedCallback } from '../../../util/use_debounced_callback.ts';
import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { HymnRowHeight, HymnsList } from '../../../views/hymns_list.tsx';

export interface SongSelectorProps {
  onClose: () => void;
  onSongClick: (songSlug?: SongSlug) => void;
  songList: SongList;
  selectSearchValue?: boolean;
}

const PlayButtonDiameter = `calc(1.5 * ${HymnRowHeight})`;

export const SongSelector = ({ onClose, onSongClick, songList, selectSearchValue }: SongSelectorProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [hymnToPreview, setHymnToPreview] = useState<Hymn | undefined>();
  const hymnals = use(HymnalsContext);
  const { customMusic, customMusicManifest } = useCustomMusic();
  const [_playlist, setPlaylist] = useLocalStorage(LocalStorageKey.Playlist);
  const [_playlistIndex, setPlaylistIndex] = useLocalStorage(LocalStorageKey.PlaylistIndex);

  useEffect(() => {
    // TODO(hewitt): would be better to tie this to whether the play button is visible in the selector instead of mobile
    if (!hymnToPreview || getPlatform() === Platform.Mobile) {
      setPlaylist(undefined);
    } else {
      setPlaylist(getSongListFromHymnal({ name: defaultPlaylistName, hymns: [hymnToPreview] }));
      setPlaylistIndex(0);
    }
  }, [hymnToPreview, setPlaylist, setPlaylistIndex]);

  const { sequencer, playing: isPlaying, readyToPlay, setOverrideVoiceValuesWithDefaults } = use(SequencerContext);

  const { setSearchString } = use(SearchContext);
  useEffectOnce(() => {
    setOverrideVoiceValuesWithDefaults(true);
    // always clear the search string & playlist when we close the song selector (should also do this for search)
    return () => {
      setSearchString('');
      setPlaylist(undefined);
      setOverrideVoiceValuesWithDefaults(false);
    };
  });

  // TODO(hewitt): set voice values to default
  //       voiceValues: getDefaultValue(LocalStorageKey.VoiceValues),

  const onClickBackground = useCallback(
    (event: MouseEvent) => {
      if (event.target === dialogRef.current) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    const controller = new AbortController();
    const { current } = dialogRef;
    current?.showModal();
    current?.addEventListener('click', onClickBackground, { signal: controller.signal });
    return () => controller.abort();
  }, [dialogRef, onClickBackground]);

  const onPreviewHymn = useCallback(
    (hymn: Hymn | undefined) => {
      if (hymn) {
        const { copyrights: _, ...hymnNoCopyrights } = hymn;
        if (JSON.stringify(hymnToPreview) !== JSON.stringify(hymnNoCopyrights)) {
          setHymnToPreview(hymnNoCopyrights);
        }
      } else {
        setHymnToPreview(undefined);
      }
    },
    [hymnToPreview],
  );

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, searchString?: string) => {
      if (event.key === 'ArrowRight' && (event.target as HTMLInputElement).selectionStart === searchString?.length) {
        event.stopPropagation();
        event.preventDefault();
        if (!readyToPlay) {
          return;
        }
        if (isPlaying) {
          sequencer.pause();
        } else {
          sequencer.play();
        }
      }
    },
    [isPlaying, readyToPlay, sequencer],
  );

  // TODO(hewitt): render this locally instead of passing into hymnals list once hymns list is componentized
  const headerAfterFirstHymnal = <LibraryHeader>Library</LibraryHeader>;

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  const onAddSong = useCallback(
    async (songName?: string) => {
      localStorageSet(LocalStorageKey.MusicUploadSongName, songName);
      const { songSlug } = await addCustomSong({ customMusic, customMusicManifest, defaultSongName: songName });
      if (!songSlug) {
        return;
      }
      onSongClick(songSlug);
    },
    [customMusic, customMusicManifest, onSongClick],
  );

  return (
    <SongSelectorDialog ref={dialogRef} onKeyDown={onKeyDown}>
      <OuterWrapper>
        <HymnsListWrapper>
          <HymnsList
            hymnals={hymnals}
            title={'Song Selector'}
            activateSearch={true}
            onSongClick={onSongClick}
            onClose={onClose}
            onAddSong={onAddSong}
            isInSongSelector={true}
            selectSearchValue={selectSearchValue}
            onPreviewHymn={onPreviewHymn}
            onSearchKeyDownProp={onSearchKeyDown}
            deleteButtonVisible={getPlatform() === Platform.Mobile || songList.type === SongListType.HymnOfTheMonth}
            playButton={!readyToPlay ? undefined : <SelectorPlayButton />}
            headerAfterFirstHymnal={headerAfterFirstHymnal}
          />
        </HymnsListWrapper>
        {hymnToPreview && <PreviewPane />}
      </OuterWrapper>
    </SongSelectorDialog>
  );
};

const previewPaneVisibilityBreakpoint = '900px';
const songSelectorHeight = '95vh';
const songSelectorVerticalPadding = '10px';

const SongSelectorDialog = styled.dialog`
  height: ${songSelectorHeight};
  width: 95vw;
  margin: auto;
  border-radius: 15px;
  background-color: var(--color-background);
  border: none;
  overflow: hidden;
  padding: ${songSelectorVerticalPadding} 20px;
  @media (max-width: ${previewPaneVisibilityBreakpoint}) {
    max-width: 500px;
  }
`;

const OuterWrapper = styled.div`
  display: flex;
  height: 100%;
`;

const HymnsListWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;

function PreviewPane() {
  const pdfWrapperRef = useRef<HTMLDivElement>(null);

  return (
    <PreviewPaneWrapper>
      <PdfWrapper ref={pdfWrapperRef}>
        <PdfViewer isInSongSelector={true} />
      </PdfWrapper>
      <TempoAdjuster />
    </PreviewPaneWrapper>
  );
}

const PreviewPaneWrapper = styled.div`
  position: relative;
  @media (max-width: ${previewPaneVisibilityBreakpoint}) {
    display: none;
  }
  flex: 2;
  padding-left: 20px;
`;

const PdfWrapper = styled.div`
  height: 100%;
`;

function SelectorPlayButton() {
  const { hymn, networkError, sequencer } = use(SequencerContext);

  useEffect(() => {
    sequencer.pause(); // pause player when mouse hovers over other songs
  }, [hymn, sequencer]);

  return networkError &&
    [NetworkError.MidiDownloadFailed, NetworkError.MidiFileParseFailed].includes(networkError) ? null : (
    <PlayButtonOuterWrapper>
      <PlayButtonRightBorderCover />
      <PlayButtonInnerWrapper>
        <PlayButton noShadow={true} />
      </PlayButtonInnerWrapper>
    </PlayButtonOuterWrapper>
  );
}

SelectorPlayButton.displayName = 'SelectorPlayButton';

const PlayButtonOuterWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

const PlayButtonInnerWrapper = styled.div`
  position: relative;
  width: ${PlayButtonDiameter};
  height: ${PlayButtonDiameter};
`;

const LibraryHeader = styled.h1`
  font-family: Jost-SemiBold, Arial, sans-serif;
  padding-top: 0.7lh;
  padding-left: ${hymnalEntryPadding};
  color: var(--color-text);
`;

const PlayButtonRightBorderCover = styled.div`
  position: absolute;
  /* Center vertically */
  top: 50%;
  transform: translateY(-50%);
  right: 0;
  height: calc((${HymnRowHeight}) + 2px);
  width: calc((${PlayButtonDiameter}) / 2);
  background-color: var(--color-background);
`;

// Every tempo save upserts the org and therefore lands in the audit log, so a burst of spinner clicks
// must collapse into a single write. 1.5s spans both a rapid click burst and the slower cadence of a
// deliberate clicker (or the pauses between digits when a tempo is typed), while still saving long
// before the admin moves on. Nothing is lost while waiting - the save is flushed on song change,
// unmount, and page hide (see below).
const tempoSaveDebounceMs = 1500;

function TempoAdjuster() {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const { hymn, defaultTempo, setTempoCenterShift, onTempoChange } = use(SequencerContext);
  const songSlug = hymn?.slug;

  // The input is normally driven by the org's saved tempo, so while a save is pending we drive it from
  // the value the admin picked instead - otherwise React would snap the spinner back to the saved tempo.
  const [unsavedTempo, setUnsavedTempo] = useState<number | undefined>();
  const displayedTempo = unsavedTempo ?? defaultTempo;

  const saveTempo = useCallback(
    (songSlugToSave: SongSlug, songTempo: number) => {
      setUnsavedTempo(undefined);
      if (!currentOrg) {
        return;
      }
      // the saved tempo becomes the new playback baseline, so drop the shift that was standing in for it
      setTempoCenterShift(0);
      void updateOrgInfo(setSongTempoForOrg({ org: currentOrg, songSlug: songSlugToSave, songTempo }));
    },
    [currentOrg, setTempoCenterShift, updateOrgInfo],
  );

  const { call: saveTempoSoon, flush: flushTempoSave } = useDebouncedCallback(saveTempo, tempoSaveDebounceMs);

  // A pending save carries the slug of the song it was made for, so flushing here writes the previous
  // song's tempo to the previous song. Also covers unmount (closing the song picker).
  useEffect(() => flushTempoSave, [songSlug, flushTempoSave]);

  // closing the laptop lid or backgrounding the tab must not drop the tempo the admin just set
  useEffect(() => {
    const controller = new AbortController();
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        flushTempoSave();
      }
    };
    window.addEventListener('pagehide', flushTempoSave, { signal: controller.signal });
    document.addEventListener('visibilitychange', flushWhenHidden, { signal: controller.signal });
    return () => controller.abort();
  }, [flushTempoSave]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const songTempo = Number((event.target as HTMLInputElement).value);
      // playback follows the admin immediately - only the org write is deferred
      setTempoCenterShift(songTempo - defaultTempo);
      onTempoChange();
      if (!songSlug) {
        return;
      }
      setUnsavedTempo(songTempo);
      saveTempoSoon(songSlug, songTempo);
    },
    [defaultTempo, onTempoChange, saveTempoSoon, setTempoCenterShift, songSlug],
  );

  return (
    <TempoWrapper>
      <QuarterNote width={'10px'} />
      <TempoEquals>=</TempoEquals>
      <TempoValue
        type="number"
        value={displayedTempo}
        $charWidth={displayedTempo?.toString().length ?? 2}
        onChange={onChange}
      />
    </TempoWrapper>
  );
}

const TempoWrapper = styled.div`
  z-index: 2;
  position: absolute;
  top: 40px;
  left: 65px;
  padding: 8px 20px;
  border-radius: 15px;
  background-color: color-mix(in srgb, var(--color-background) 85%, transparent);
  color: var(--color-text);
  display: flex;
  gap: 5px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.2rem;
`;

const TempoValue = styled.input<{ $charWidth: number }>`
  width: calc(${(props) => props.$charWidth}ch + 1.5ch);
  text-align: center;
  border: none;
  padding: 0;
  &::-webkit-inner-spin-button {
    opacity: 1;
  }
`;

const TempoEquals = styled.div`
  padding-left: 0.3ch;
`;
